import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Spinner } from "@depmap/common-components";
import SliceTable from "@depmap/slice-table";
import { areSliceQueriesEqual, SliceQuery } from "@depmap/types";
import { usePlotlyLoader } from "../../../../contexts/PlotlyLoaderContext";
import { isCompleteExpression } from "../../../../utils/dimensionHelpers";
import {
  DEFAULT_EMPTY_EXPR,
  useContextBuilderState,
} from "../../state/ContextBuilderState";
import NumberOfMatches from "../Expression/NumberOfMatches";
import useMatches from "../../hooks/useMatches";
import confirmManualSelectMode from "./confirmManualSelectMode";
import styles from "../../../../styles/ContextBuilderV2.scss";

function ContextBuilderTableView() {
  const {
    dimension_type,
    isManualSelectMode,
    mainExpr,
    name,
    replaceExprWithSimpleList,
    setIsReadyToSave,
    setShowTableView,
    setTableOnlySlices,
    tableOnlySlices,
    undoManualSelectionMode,
    uniqueVariableSlices,
  } = useContextBuilderState();

  const PlotlyLoader = usePlotlyLoader();
  const [isViewInitialized, setIsViewInitialized] = useState(false);
  const { isLoading, matchingIds } = useMatches(mainExpr);
  const wasLoading = useRef(false);

  useEffect(() => {
    if (mainExpr === DEFAULT_EMPTY_EXPR) {
      setIsViewInitialized(true);
    } else if (!isCompleteExpression(mainExpr)) {
      setIsViewInitialized(false);
    }
  }, [mainExpr]);

  useEffect(() => {
    if (isLoading) {
      setIsReadyToSave(false);
    } else {
      setIsReadyToSave(matchingIds.length > 0);

      if (wasLoading.current) {
        setIsViewInitialized(true);
      }
    }

    wasLoading.current = isLoading;
  }, [isLoading, matchingIds, setIsReadyToSave]);

  const handleChangeSlices = useCallback(
    (updatedSlices: SliceQuery[]) => {
      const tableSlices = updatedSlices.filter((slice) => {
        return !uniqueVariableSlices.find((varSlice) =>
          areSliceQueriesEqual(varSlice, slice)
        );
      });

      // Bail out when nothing actually changed. SliceTable may re-fire
      // onChangeSlices on prop-identity churn (e.g. when handleChangeSlices
      // itself gets a new identity after `uniqueVariableSlices` recomputes),
      // and blindly calling setTableOnlySlices with a fresh array would
      // feed a re-render loop.
      setTableOnlySlices((prev) => {
        if (
          prev.length === tableSlices.length &&
          prev.every((s, i) => areSliceQueriesEqual(s, tableSlices[i]))
        ) {
          return prev;
        }
        return tableSlices;
      });
    },
    [uniqueVariableSlices, setTableOnlySlices]
  );

  const shouldConfirmRowSelection = useRef(false);
  // The user's latest selection. Needed because `matchingIds` lags behind it:
  // it still holds the rule-based matches until useMatches resolves for the
  // new simple-list expression.
  const manualSelection = useRef<string[] | null>(null);

  const handleChangeRowSelection = useCallback(
    (nextRowSelection: Record<string, boolean>) => {
      const selectedIds = Object.entries(nextRowSelection)
        .filter(([, included]) => included)
        .map(([id]) => id);

      manualSelection.current = selectedIds;
      replaceExprWithSimpleList(selectedIds);
      shouldConfirmRowSelection.current = true;
    },
    [replaceExprWithSimpleList]
  );

  const sliceTableRef = useRef<{
    forceInitialize: (options?: { preserveSortOrder?: boolean }) => void;
  }>(null);
  const shouldInitTable = useRef(false);

  useEffect(() => {
    if (isManualSelectMode && shouldConfirmRowSelection.current) {
      shouldConfirmRowSelection.current = false;
      // Keep rows where they are, so a row you just deselected doesn't jump.
      sliceTableRef.current?.forceInitialize({ preserveSortOrder: true });

      confirmManualSelectMode().then((confirmed) => {
        if (!confirmed) {
          shouldInitTable.current = true;
          setIsViewInitialized(false);
          undoManualSelectionMode();
        }
      });
    }
  }, [isManualSelectMode, undoManualSelectionMode]);

  useEffect(() => {
    if (shouldInitTable.current) {
      shouldInitTable.current = false;
      sliceTableRef.current?.forceInitialize();
    }
  }, [isManualSelectMode]);

  if (!isViewInitialized) {
    return (
      <div className={styles.ContextBuilderTableView}>
        <div className={styles.spinner}>
          <Spinner position="absolute" left="calc(50vw - 100px)" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.ContextBuilderTableView}>
      <SliceTable
        PlotlyLoader={PlotlyLoader}
        sliceTableRef={sliceTableRef}
        getInitialState={() => {
          return {
            initialSlices: [...uniqueVariableSlices, ...tableOnlySlices],
            viewOnlySlices: new Set(uniqueVariableSlices),
            initialRowSelection: Object.fromEntries(
              (isManualSelectMode && manualSelection.current
                ? manualSelection.current
                : matchingIds
              ).map((id) => [id, true])
            ),
          };
        }}
        downloadFilename={name}
        index_type_name={dimension_type}
        onChangeSlices={handleChangeSlices}
        allowSameTypeContextCreation={false}
        enableRowSelection
        enableMultiRowSelection
        onChangeRowSelection={handleChangeRowSelection}
        renderCustomControls={() => {
          return isManualSelectMode ? (
            <div className={styles.manualSelectionControls}>
              You’re now in manual selection mode.{" "}
              <Button
                bsStyle="info"
                onClick={() => {
                  shouldInitTable.current = true;
                  setIsViewInitialized(false);
                  undoManualSelectionMode();
                }}
              >
                Restore previous rules
              </Button>
            </div>
          ) : null;
        }}
        renderCustomActions={() => {
          return (
            <div className={styles.customTableControls}>
              <Button onClick={() => setShowTableView(false)}>
                <i className="glyphicon glyphicon-tasks" />
                <span> View as rules</span>
              </Button>
              <NumberOfMatches expr={mainExpr} showNumCandidates />
            </div>
          );
        }}
      />
    </div>
  );
}

export default ContextBuilderTableView;
